import {
  ButtonBack,
  ButtonNext,
  Dot,
  CarouselProvider,
  Slide,
  Slider,
} from "pure-react-carousel"
import "pure-react-carousel/dist/react-carousel.es.css"
import React, { createRef, useState } from "react"
import { useTranslation } from "react-i18next"

import Image from "@atoms/Image/Image"
import ImageCarouselCurrentSlide from "@atoms/ImageCarouselCurrentSlide/ImageCarouselCurrentSlide"
import ModalWindow from "@atoms/ModalWindow/ModalWindow"
import "@lang/i18n"

import styles from "./ImageCarousel.module.scss"

const ImageCarousel = ({
  heading,
  slides,
  aspectRatio,
  zoomable,
  thumbnails,
  shadow,
  vignette,
}) => {
  const { t } = useTranslation()
  const [imageModalOpen, setImageModalOpen] = useState([])

  const aspectRatioArray = aspectRatio ? aspectRatio.split(":") : [16, 9]
  const paddingBottom =
    aspectRatioArray.length === 2
      ? (aspectRatioArray[1] / aspectRatioArray[0]) * 100
      : "75"

  const sliderItems = Array.isArray(slides)
    ? slides.map((slide, index) => {
        const slideWidth = `${
          (slide?.mediaDetails?.width / slide?.mediaDetails?.height) * 90
        }vh`

        const link = createRef()

        // Why proxylink?
        // "It's not disastrous in terms of comprehension, but verbose..."
        // (https://inclusive-components.design/cards/)

        const proxyLink = () => {
          if (
            link.current &&
            typeof window !== "undefined" &&
            window?.innerWidth > 767
          ) {
            link.current.click()
          }
        }

        const switchActiveModal = (newIndex) => {
          if (newIndex > slides.length - 1) {
            setImageModalOpen({ 0: true })
          } else if (newIndex < 0) {
            setImageModalOpen({ [slides.length - 1]: true })
          } else {
            setImageModalOpen({ [newIndex]: true })
          }
        }

        return (
          <Slide index={index} key={index} tabIndex={-1}>
            {zoomable && (
              <>
                {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-static-element-interactions */}
                <div
                  className={`${styles.zoomable} ${
                    vignette ? styles.vignette : undefined
                  }`}
                  onClick={proxyLink}
                >
                  <Image
                    image={slide}
                    widths={[480, 740, 1010]}
                    aspectRatio={aspectRatio}
                  />
                  <button
                    onClick={() => setImageModalOpen({ [index]: true })}
                    ref={link}
                  >
                    <img src={"/media/svg/zoom.svg"} alt={t("Zoom")} />
                  </button>
                </div>
                <ModalWindow
                  open={imageModalOpen[index]}
                  setOpen={setImageModalOpen}
                  label={slide.alt}
                  modalStyle={{ textAlign: "center", width: "96vw" }}
                  margins={false}
                  type="gallery"
                >
                  <div
                    className={styles.modalWrapper}
                    style={{ width: slideWidth }}
                  >
                    <Image image={slide} widths={[800, 1500]} />
                    <button
                      className={styles.prev}
                      onClick={() => switchActiveModal(index - 1)}
                    >
                      {t("Previous")}
                    </button>
                    <button
                      className={styles.next}
                      onClick={() => switchActiveModal(index + 1)}
                    >
                      {t("Next")}
                    </button>
                  </div>
                </ModalWindow>
              </>
            )}
            {!zoomable && (
              <div className={vignette ? styles.vignette : undefined}>
                <Image
                  image={slide}
                  widths={[480, 740, 1010]}
                  aspectRatio={aspectRatio}
                />
              </div>
            )}
          </Slide>
        )
      })
    : []

  const thumbnailItems =
    Array.isArray(slides) && thumbnails
      ? slides.map((slide, index) => {
          return (
            <div className={styles.thumbnailSingle} key={index}>
              <Dot slide={index} />
              <Image image={slide} widths={[120, 240]} aspectRatio={"3:2"} />
            </div>
          )
        })
      : []

  return (
    <>
      <section
        className={`${styles.element} ${
          slides && slides.length > 1 ? styles.controls : null
        } ${thumbnails ? styles.hasThumbnails : ""} ${
          thumbnails ? styles.hasThumbnails : ""
        } make-full-width-on-mobile`}
      >
        {heading && <h2>{heading}</h2>}
        <CarouselProvider
          className={`${styles.carousel} ${shadow ? styles.shadow : null}`}
          totalSlides={slides ? slides.length : 1}
          visibleSlides={1}
          step={1}
          infinite={true}
          //touchEnabled={!zoomable}
          dragEnabled={false}
        >
          <Slider style={{ paddingBottom: paddingBottom + "%" }}>
            {sliderItems}
          </Slider>
          {slides && slides.length > 1 && (
            <>
              {thumbnails && (
                <div className={styles.thumbnailsContainer}>
                  <>
                    <ButtonBack>{t("Back")}</ButtonBack>
                    <ButtonNext>{t("Next")}</ButtonNext>
                  </>
                  <div className={styles.thumbnails}>
                    <div>{thumbnailItems}</div>
                  </div>
                </div>
              )}
              {!thumbnails && (
                <>
                  <ButtonBack>{t("Back")}</ButtonBack>
                  <ButtonNext>{t("Next")}</ButtonNext>
                </>
              )}
              <div
                className={`${styles.current} ${
                  thumbnails ? styles.hasThumbnails : ""
                }`}
              >
                <ImageCarouselCurrentSlide />
              </div>
            </>
          )}
        </CarouselProvider>
      </section>
    </>
  )
}

export default ImageCarousel
